/**
 * The Phase 1 `MessageId` set — the stable-id vocabulary (Agent 6 ↔ Agent 5 contract).
 *
 * INV-STABLE-ID (MathToolImplementation.md §10, Architecture_V2.01.md §6): every
 * human-facing string is the image of a stable id. The view-model (L4) carries these
 * ids — never final strings — and L5 resolves them through a `Catalog`. Because the
 * verdict factors through the id (`stable_id_translation_inert` /
 * `verdict_independent_of_label`, ArchitectureV2.lean §3), swapping the catalog leaves
 * every verdict fixed.
 *
 * This module DEFINES the Phase 1 slice of that vocabulary (the §9.1 fixture labels plus
 * axis names/grades, verdict states, lane names, constraint statuses, precision-loss
 * messages and register names). Agent 5's view-model keys map cleanly onto these ids; the
 * English `Catalog` (catalog-en.ts) must cover every id listed here (asserted in tests).
 *
 * DESIGN NOTE (DECISIONS_Agent6.md D2): keys are dotted, hierarchical strings grouped by
 * domain. They are *opaque stable ids*, not English — a mistranslation catalog can map them
 * to anything and the verdict is unmoved. The grouping is purely for human navigation.
 */
import { messageId } from '@progression/contracts';
/**
 * The ergonomic grouped reference. Every value is a raw id string; pass through
 * {@link messageId} (or use {@link MSG_ID}) when a branded `MessageId` is required.
 */
export const MSG = {
    // --- application chrome ---
    app: {
        title: 'app.title',
        configDefault: 'config.default',
    },
    // --- registers (the full / plain / kid frame, §5) ---
    register: {
        full: 'register.full',
        plain: 'register.plain',
        kid: 'register.kid',
        lossy: 'register.lossy', // plain/kid is a lossy frame (framing_lossy)
    },
    // --- the three axes (W / E / M identity, never blurred) ---
    axis: {
        warrant: 'axis.warrant',
        empirical: 'axis.empirical',
        modal: 'axis.modal',
        // parametric grade labels (exercise ICU number formatting)
        warrantGrade: 'axis.warrant.grade', // params: { level }
        modalGrade: 'axis.modal.grade', // params: { level }
    },
    // --- empirical taxonomy (Emp) — a taxonomy, not a rank ---
    empirical: {
        defn: 'empirical.defn',
        interp: 'empirical.interp',
        testable: 'empirical.testable',
        tested: 'empirical.tested',
        refuted: 'empirical.refuted',
        program: 'empirical.program',
        philos: 'empirical.philos',
    },
    // --- verdict states (VerdictState + L4 'outside') ---
    verdict: {
        explorable: 'verdict.explorable',
        refuted: 'verdict.refuted',
        outside: 'verdict.outside',
        summary: 'verdict.summary', // params: { state } — ICU select
        refutedDetail: 'verdict.refuted.detail', // params: { count } — ICU plural
        missingDetail: 'verdict.missing.detail', // params: { count } — ICU plural
    },
    // --- the three integrity lanes (never merged into a score) ---
    lane: {
        machine: 'lane.machine',
        expert: 'lane.expert',
        community: 'lane.community',
        machineVerified: 'lane.machine.verified',
        machineUnverified: 'lane.machine.unverified',
        machineConflict: 'lane.machine.conflict',
        expertAudited: 'lane.expert.audited',
        expertInProgress: 'lane.expert.in-progress',
        expertNotReviewed: 'lane.expert.not-reviewed',
        communityFlags: 'lane.community.flags',
        communityNone: 'lane.community.none',
    },
    // --- constraint statuses (the no-ambiguity calculus) ---
    status: {
        adoptedFormal: 'status.adopted-formal',
        adoptedInterpretive: 'status.adopted-interpretive',
        suspended: 'status.suspended',
    },
    // --- §9.1 fixture: constraint labels ---
    constraint: {
        logic: 'constraint.logic',
        lightBends: 'constraint.light-bends',
        parallelPostulate: 'constraint.parallel-postulate',
    },
    // --- §9.1 fixture: claim labels ---
    claim: {
        scalarGravity: 'claim.scalar-gravity',
        nonEuclidean: 'claim.non-euclidean',
        substrateSr: 'claim.substrate-sr',
    },
    // --- §9.1 fixture: edge source labels ---
    edge: {
        extObservation: 'edge.ext-observation',
        resourceSample: 'edge.resource-sample',
        heuristicSample: 'edge.heuristic-sample',
        sharedSample: 'edge.shared-sample',
    },
    // --- precision-loss messages (A3: typed, indexable) ---
    precision: {
        kindRounding: 'precision.kind.rounding',
        kindSimplification: 'precision.kind.simplification',
        kindRegister: 'precision.kind.register',
        kindTranslation: 'precision.kind.translation',
        kindExport: 'precision.kind.export',
        severityLow: 'precision.severity.low',
        severityMedium: 'precision.severity.medium',
        severityHigh: 'precision.severity.high',
        note: 'precision.note', // params: { severity, kind } — ICU select
        summary: 'precision.summary', // params: { count } — ICU plural
        lastUpdated: 'precision.last-updated', // params: { when } — ICU date
    },
};
/** Recursively collect the leaf string values of {@link MSG}. */
function collectIds(node) {
    const out = [];
    for (const value of Object.values(node)) {
        if (typeof value === 'string')
            out.push(value);
        else if (value && typeof value === 'object')
            out.push(...collectIds(value));
    }
    return out;
}
/**
 * Every Phase 1 `MessageId` (raw string form), de-duplicated and sorted. The English
 * catalog must cover exactly this set (tested in catalog.test.ts). This is the canonical
 * list Agent 5 codes the view-model against.
 */
export const PHASE1_MESSAGE_IDS = Object.freeze(Array.from(new Set(collectIds(MSG))).sort());
/** The branded-id form of {@link PHASE1_MESSAGE_IDS}. */
export const PHASE1_MESSAGE_ID_SET = new Set(PHASE1_MESSAGE_IDS);
/** Brand a known id string as a `MessageId`. */
export const MSG_ID = (id) => messageId(id);
//# sourceMappingURL=message-ids.js.map